import React, { useEffect, useRef } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  title?: string;
  fullHeight?: boolean;
}

export function BottomSheet({ open, onClose, children, title, fullHeight }: Props) {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (sheetRef.current && !sheetRef.current.contains(e.target as Node)) onClose();
    };
    const t = setTimeout(() => document.addEventListener('mousedown', handler), 50);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', handler); };
  }, [open, onClose]);

  return (
    <div
      className="fixed inset-0 z-[2000] flex flex-col justify-end items-center"
      style={{ pointerEvents: open ? 'auto' : 'none' }}
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/60 transition-opacity duration-300 ${open ? 'opacity-100' : 'opacity-0'}`}
      />

      {/* Sheet — constrained on desktop */}
      <div
        ref={sheetRef}
        style={{ willChange: 'transform', background: 'var(--sheet-bg)', borderTop: '1px solid var(--nav-border)', boxShadow: '0 -4px 32px rgba(124,58,237,0.12)' }}
        className={`relative w-full md:max-w-lg flex flex-col rounded-t-2xl shadow-sheet transition-transform duration-250 ease-out ${
          fullHeight ? 'max-h-[92vh]' : 'max-h-[80vh]'
        } ${open ? 'translate-y-0' : 'translate-y-full'}`}
      >
        {/* Handle + optional close button row */}
        <div className="flex items-center justify-between pt-3 pb-1 px-4 flex-shrink-0">
          <div className="w-8" /> {/* spacer to keep handle centred */}
          <div className="w-10 h-1 rounded-full" style={{ background: 'rgba(124,58,237,0.20)' }} />
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-stabeon-muted hover:text-stabeon-text hover:bg-white/10 transition-all active:scale-90"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {title && (
          <div className="px-5 pb-3 border-b border-white/6 flex-shrink-0">
            <h2 className="text-stabeon-text font-semibold text-base">{title}</h2>
          </div>
        )}

        <div className="overflow-y-auto flex-1 px-5 py-4">
          {children}
        </div>
      </div>
    </div>
  );
}
